import React from 'react';
import { Typography } from 'antd';

const { Title, Paragraph } = Typography;

export const PrivacyContentES: React.FC = () => {
  return (
    <div className="terms-content">
      <Paragraph className="terms-intro">
        Esta Política de Privacidad (la "Política") describe cómo DivvyStash recopila, usa, divulga y protege 
        la información sobre los usuarios de la plataforma DivvyStash y servicios relacionados (colectivamente, el "Servicio").
      </Paragraph>

      <Paragraph className="terms-intro">
        Al acceder o usar el Servicio, usted acepta las prácticas descritas en esta Política. Si no está de acuerdo 
        con esta Política, no debe usar el Servicio.
      </Paragraph>

      <Title level={2} className="section-title">1. Alcance</Title>
      <Paragraph>Esta Política se aplica a la información que recopilamos:</Paragraph>
      <ul className="terms-list">
        <li>Cuando accede o usa la aplicación web DivvyStash o cualquier otra interfaz que proporcionamos;</li>
        <li>Cuando se comunica con nosotros (como por correo electrónico o canales de soporte);</li>
        <li>Cuando un Administrador envía información sobre usted para invitarlo al Servicio.</li>
      </ul>
      <Paragraph>
        Esta Política no se aplica a sitios web, servicios o aplicaciones de terceros, incluso si se accede a través del Servicio.
      </Paragraph>

      <Title level={2} className="section-title">2. Información que Recopilamos</Title>
      <Paragraph>
        Recopilamos información de tres maneras principales: (a) información que nos proporciona, (b) información que 
        recopilamos automáticamente cuando usa el Servicio, y (c) información que recibimos de Administradores u otras terceras partes.
      </Paragraph>

      <Title level={3} className="subsection-title">2.1 Información que Proporciona</Title>
      <Paragraph>Podemos recopilar las siguientes categorías de información que nos proporciona directamente:</Paragraph>
      
      <Paragraph><strong>Información de Cuenta y Perfil.</strong> Su nombre, dirección de correo electrónico, contraseña (almacenada 
      en forma cifrada), país de residencia, idioma o configuración regional preferida, y cualquier detalle adicional del perfil que 
      elija proporcionar.</Paragraph>

      <Paragraph><strong>Información de Contribución y Participación.</strong> Cuando participa como Contribuyente, 
      recopilamos y mantenemos registros relacionados con su participación, tales como:</Paragraph>
      <ul className="terms-list">
        <li>Contribución mínima requerida establecida por el Administrador;</li>
        <li>Montos de contribución confirmados y capital base;</li>
        <li>Preferencias de porcentaje de reinversión;</li>
        <li>Asignaciones de ganancias y registros de liquidación asociados con su cuenta;</li>
        <li>Solicitudes de retiro, incluyendo montos y estado (solicitado, aprobado, rechazado, completado o cancelado).</li>
      </ul>

      <Paragraph><strong>Información del Administrador.</strong> Si usted es un Administrador, recopilamos información sobre su 
      rol de Administrador, incluyendo información sobre los Usuarios que invita, configuraciones de contribución mínima y entradas 
      de ganancias que crea.</Paragraph>

      <Paragraph><strong>Comunicaciones y Soporte.</strong> Si se comunica con nosotros (por ejemplo, por correo electrónico o 
      a través de un formulario de soporte), recopilamos el contenido de su mensaje y cualquier detalle de contacto que proporcione.</Paragraph>

      <Title level={3} className="subsection-title">2.2 Información que Recopilamos Automáticamente</Title>
      <Paragraph>
        Cuando accede o usa el Servicio, podemos recopilar automáticamente cierta información sobre su dispositivo y uso, incluyendo:
      </Paragraph>

      <Paragraph><strong>Información de Dispositivo y Registro.</strong> Dirección IP, tipo de navegador, sistema operativo, 
      identificadores de dispositivo, páginas vistas, fechas y horas de acceso, páginas de referencia/salida y otros datos de registro estándar.</Paragraph>

      <Paragraph><strong>Información de Uso.</strong> Acciones que realiza dentro del Servicio, como ver paneles, actualizar su 
      porcentaje de reinversión, enviar solicitudes de retiro o cambiar la configuración del perfil.</Paragraph>

      <Paragraph><strong>Información de Idioma y Configuración Regional.</strong> La configuración de idioma de su navegador y las 
      preferencias de idioma/configuración regional en la aplicación, que nos ayudan a presentar contenido en el idioma apropiado.</Paragraph>

      <Paragraph>
        Podemos usar cookies, almacenamiento local y tecnologías similares para recopilar y almacenar parte de esta información. 
        Consulte la Sección 6 (Cookies y Tecnologías Similares) a continuación para más detalles.
      </Paragraph>

      <Title level={3} className="subsection-title">2.3 Información de Administradores y Terceros</Title>
      <Paragraph>Podemos recibir información sobre usted de:</Paragraph>

      <Paragraph><strong>Administradores.</strong> Los Administradores pueden proporcionar su nombre, dirección de correo electrónico, 
      país y requisito de contribución mínima para invitarlo al Servicio y administrar su participación.</Paragraph>

      <Paragraph><strong>Proveedores de Servicios.</strong> Podemos recibir información limitada de proveedores externos que 
      proporcionan infraestructura, análisis, entrega de correo electrónico u otros servicios (por ejemplo, estado de entrega de 
      correo electrónico, información de rebote o estadísticas agregadas).</Paragraph>

      <Paragraph><strong>Otras Fuentes.</strong> Donde lo permita la ley, podemos recibir información de fuentes públicas o de 
      terceros que ayudan con la prevención de fraude, seguridad o cumplimiento.</Paragraph>

      <Title level={2} className="section-title">3. Cómo Usamos la Información</Title>
      <Paragraph>Usamos la información que recopilamos para los siguientes propósitos:</Paragraph>

      <Title level={3} className="subsection-title">3.1 Para Proporcionar y Mantener el Servicio</Title>
      <ul className="terms-list">
        <li>Crear y administrar cuentas de usuario y perfiles;</li>
        <li>Permitir a los Administradores invitar Usuarios y configurar ajustes de contribución;</li>
        <li>Registrar y mostrar contribuciones, capital base, asignaciones de ganancias, porcentajes de reinversión, 
        liquidaciones semanales y saldos retirables;</li>
        <li>Procesar y rastrear solicitudes de retiro y su estado;</li>
        <li>Proporcionar paneles de usuario y vistas generales de administrador, incluyendo resúmenes de rendimiento 
        (semana hasta la fecha, mes hasta la fecha, año hasta la fecha y ganancias totales).</li>
      </ul>

      <Title level={3} className="subsection-title">3.2 Para Comunicarse con Usted</Title>
      <ul className="terms-list">
        <li>Enviar correos electrónicos transaccionales, como invitaciones, correos electrónicos de restablecimiento de contraseña, 
        confirmaciones de contribución, notificaciones de solicitud de retiro y resúmenes de liquidación semanal;</li>
        <li>Responder a sus preguntas, comentarios o solicitudes de soporte;</li>
        <li>Enviar avisos administrativos o relacionados con el servicio, como cambios a esta Política o nuestros Términos.</li>
      </ul>

      <Title level={3} className="subsection-title">3.3 Para Personalizar la Experiencia</Title>
      <ul className="terms-list">
        <li>Presentar el Servicio en su idioma o configuración regional preferida;</li>
        <li>Personalizar cierto contenido según su rol (Administrador vs Contribuyente) y configuración de cuenta.</li>
      </ul>

      <Title level={3} className="subsection-title">3.4 Para Análisis y Mejora del Servicio</Title>
      <ul className="terms-list">
        <li>Analizar tendencias y patrones de uso para mejorar la funcionalidad, el rendimiento y la experiencia del usuario del Servicio;</li>
        <li>Desarrollar nuevas características y ofertas.</li>
      </ul>

      <Title level={3} className="subsection-title">3.5 Para Seguridad, Prevención de Fraude y Cumplimiento</Title>
      <ul className="terms-list">
        <li>Proteger la seguridad e integridad del Servicio;</li>
        <li>Detectar, investigar y prevenir actividad fraudulenta o ilegal, uso indebido del Servicio o violaciones de nuestros Términos;</li>
        <li>Cumplir con obligaciones legales, requisitos regulatorios y solicitudes de las fuerzas del orden, cuando corresponda.</li>
      </ul>

      <Title level={3} className="subsection-title">3.6 Con Su Consentimiento</Title>
      <Paragraph>
        Para cualquier otro propósito divulgado en el momento de la recopilación, donde solicitamos y usted proporciona su consentimiento.
      </Paragraph>

      <Title level={2} className="section-title">4. Bases Legales para el Procesamiento (Usuarios de EEE/Reino Unido)</Title>
      <Paragraph>
        Si se encuentra en el Espacio Económico Europeo (EEE), el Reino Unido u otra jurisdicción que requiere una base legal 
        para procesar datos personales, nuestras bases legales incluyen:
      </Paragraph>

      <Paragraph><strong>Contrato.</strong> Procesamiento que es necesario para proporcionar el Servicio y cumplir con nuestras 
      obligaciones contractuales hacia usted (por ejemplo, crear y mantener su cuenta, registrar su participación y proporcionar 
      paneles y resúmenes).</Paragraph>

      <Paragraph><strong>Intereses Legítimos.</strong> Procesamiento para nuestros intereses legítimos, incluyendo asegurar y 
      mejorar el Servicio, prevenir fraude, comprender cómo se usa el Servicio y apoyar a los Administradores en la administración 
      de la participación, siempre que tales intereses no sean anulados por sus derechos e intereses.</Paragraph>

      <Paragraph><strong>Obligación Legal.</strong> Procesamiento necesario para cumplir con obligaciones legales, como responder 
      a solicitudes legales de las autoridades o cumplir con requisitos de mantenimiento de registros aplicables.</Paragraph>

      <Paragraph><strong>Consentimiento.</strong> Cuando lo requiera la ley (por ejemplo, para ciertas cookies o comunicaciones 
      de marketing), podemos confiar en su consentimiento. Tiene derecho a retirar el consentimiento en cualquier momento, sin 
      afectar la legalidad del procesamiento basado en el consentimiento antes de su retiro.</Paragraph>

      <Title level={2} className="section-title">5. Cómo Compartimos la Información</Title>
      <Paragraph>No vendemos su información personal. Podemos compartir información de la siguiente manera:</Paragraph>

      <Title level={3} className="subsection-title">5.1 Con Administradores y Otros Usuarios (según Corresponda)</Title>
      <ul className="terms-list">
        <li>Los Administradores pueden ver información necesaria para administrar contribuciones, ganancias y retiros de los 
        Usuarios que administran.</li>
        <li>Los Usuarios pueden ver cierta información agregada o derivada, como su propia participación en el grupo y totales 
        generales del grupo. El diseño del Servicio no requiere que un Usuario vea los detalles personales de otros Usuarios, 
        pero algunas métricas agregadas pueden reflejar el rendimiento a nivel de grupo.</li>
      </ul>

      <Title level={3} className="subsection-title">5.2 Con Proveedores de Servicios</Title>
      <Paragraph>
        Podemos compartir información con proveedores y prestadores de servicios de terceros que realizan servicios en nuestro nombre, 
        como alojamiento, infraestructura, entrega de correo electrónico, registro, análisis y atención al cliente. Estos proveedores 
        están autorizados a usar su información solo según sea necesario para proporcionarnos servicios.
      </Paragraph>

      <Title level={3} className="subsection-title">5.3 Para Propósitos Legales y de Cumplimiento</Title>
      <Paragraph>Podemos divulgar información si creemos de buena fe que dicha divulgación es razonablemente necesaria para:</Paragraph>
      <ul className="terms-list">
        <li>Cumplir con cualquier ley, regulación, proceso legal o solicitud gubernamental aplicable;</li>
        <li>Hacer cumplir nuestros Términos u otros acuerdos;</li>
        <li>Proteger los derechos, propiedad o seguridad de DivvyStash, nuestros usuarios u otros;</li>
        <li>Detectar, prevenir o abordar de otra manera fraude, seguridad o problemas técnicos.</li>
      </ul>

      <Title level={3} className="subsection-title">5.4 Transferencias Comerciales</Title>
      <Paragraph>
        En conexión con, o durante negociaciones de, cualquier fusión, venta de activos de la empresa, financiamiento, adquisición 
        o transacción similar, podemos transferir información a otra empresa, sujeto a protecciones de confidencialidad apropiadas.
      </Paragraph>

      <Title level={3} className="subsection-title">5.5 Con Su Consentimiento</Title>
      <Paragraph>Podemos compartir información con su consentimiento o bajo su dirección.</Paragraph>

      <Title level={2} className="section-title">6. Cookies y Tecnologías Similares</Title>
      <Paragraph>Podemos usar cookies, almacenamiento local y tecnologías similares para:</Paragraph>
      <ul className="terms-list">
        <li>Mantenerlo conectado;</li>
        <li>Recordar sus preferencias (como idioma);</li>
        <li>Analizar el uso del Servicio.</li>
      </ul>
      <Paragraph>
        Por lo general, puede configurar su navegador para rechazar cookies o para indicar cuándo se está enviando una cookie. 
        Sin embargo, es posible que algunas partes del Servicio no funcionen correctamente si las cookies están deshabilitadas.
      </Paragraph>
      <Paragraph>
        Donde lo requiera la ley, obtendremos su consentimiento antes de configurar cookies no esenciales.
      </Paragraph>

      <Title level={2} className="section-title">7. Retención de Datos</Title>
      <Paragraph>Retenemos información personal durante el tiempo razonablemente necesario para:</Paragraph>
      <ul className="terms-list">
        <li>Proporcionar el Servicio y mantener su cuenta;</li>
        <li>Cumplir los propósitos descritos en esta Política;</li>
        <li>Cumplir con obligaciones legales, fiscales o contables;</li>
        <li>Resolver disputas y hacer cumplir nuestros acuerdos.</li>
      </ul>
      <Paragraph>
        El período de retención específico puede variar según el tipo de datos y los propósitos para los que los procesamos. 
        Cuando la información ya no sea necesaria para estos propósitos, la eliminaremos o anonimizaremos, sujeto a cualquier 
        limitación técnica o legal.
      </Paragraph>

      <Title level={2} className="section-title">8. Seguridad de Datos</Title>
      <Paragraph>
        Usamos medidas técnicas y organizativas razonables diseñadas para proteger la información que recopilamos y almacenamos. 
        Sin embargo, ningún método de transmisión por internet o método de almacenamiento electrónico es completamente seguro. 
        Por lo tanto, no podemos garantizar una seguridad absoluta.
      </Paragraph>
      <Paragraph>
        Usted es responsable de elegir una contraseña segura y mantener sus credenciales confidenciales. Si cree que su cuenta 
        ha sido comprometida, debe contactarnos inmediatamente.
      </Paragraph>

      <Title level={2} className="section-title">9. Transferencias Internacionales</Title>
      <Paragraph>
        DivvyStash tiene su sede en los Estados Unidos, y la información que recopilamos puede transferirse, almacenarse y 
        procesarse en ese país y en otros países donde se encuentran nuestros proveedores de servicios. Estos países pueden 
        tener leyes de protección de datos que son diferentes de las de su país de residencia.
      </Paragraph>
      <Paragraph>
        Donde lo requiera la ley, implementaremos salvaguardas apropiadas para proteger los datos personales transferidos a 
        través de fronteras, como cláusulas contractuales estándar u otros mecanismos de transferencia legales.
      </Paragraph>

      <Title level={2} className="section-title">10. Retención de Datos</Title>
      <Paragraph>
        Conservamos su información personal durante el tiempo necesario para cumplir con los propósitos descritos en esta Política, 
        cumplir con nuestras obligaciones legales, resolver disputas y hacer cumplir nuestros acuerdos.
      </Paragraph>

      <Title level={3} className="subsection-title">10.1 Registros Financieros y de Participación</Title>
      <Paragraph>
        <strong>Período de Retención: 7 años desde el cierre de la cuenta</strong>
      </Paragraph>
      <Paragraph>
        Cuando cierra su cuenta y sale del fondo, conservamos sus registros financieros y de participación durante un período 
        de <strong>7 años</strong> desde la fecha de cierre de la cuenta. Esto incluye:
      </Paragraph>
      <ul className="terms-list">
        <li>Registros de contribuciones</li>
        <li>Historial de asignaciones de ganancias</li>
        <li>Registros de liquidación semanales</li>
        <li>Solicitudes de retiro</li>
        <li>Información del saldo de la cuenta</li>
      </ul>
      <Paragraph>
        Este período de retención se alinea con los requisitos estándar de mantenimiento de registros financieros y nos asegura 
        que podamos responder a auditorías o consultas regulatorias, resolver disputas y mantener registros históricos precisos del fondo.
      </Paragraph>

      <Title level={3} className="subsection-title">10.2 Registros de Auditoría y Operativos</Title>
      <Paragraph>
        <strong>Período de Retención: Aproximadamente 1 año</strong>
      </Paragraph>
      <Paragraph>
        Los registros de auditoría del sistema y los registros operativos se conservan durante aproximadamente <strong>1 año</strong> 
        desde la fecha de creación. Estos registros incluyen:
      </Paragraph>
      <ul className="terms-list">
        <li>Eventos de autenticación de usuarios</li>
        <li>Acciones administrativas</li>
        <li>Operaciones del sistema</li>
        <li>Eventos de seguridad</li>
      </ul>
      <Paragraph>
        Los registros de auditoría con más de 1 año se eliminan automáticamente a través de un proceso de mantenimiento programado.
      </Paragraph>

      <Title level={3} className="subsection-title">10.3 Cuentas Cerradas</Title>
      <Paragraph>
        Cuando solicita cerrar su cuenta, aún puede iniciar sesión para ver sus datos históricos, pero no puede crear nuevas 
        contribuciones o solicitudes de retiro. Sus datos se conservan hasta que expire el período de retención aplicable.
      </Paragraph>
      <Paragraph>
        <strong>Nota:</strong> Estos períodos de retención se basan en prácticas comunes de la industria financiera y requisitos 
        de mantenimiento de registros fiscales. Los períodos de retención reales pueden variar según las leyes federales y estatales 
        aplicables, las regulaciones fiscales en su jurisdicción, los requisitos regulatorios y el asesoramiento legal. Esta política 
        de retención de datos puede actualizarse a medida que recibamos orientación de asesores legales y cambien las regulaciones.
      </Paragraph>

      <Title level={2} className="section-title">11. Sus Derechos y Opciones</Title>
      <Paragraph>
        Dependiendo de su ubicación, puede tener ciertos derechos con respecto a su información personal, tales como:
      </Paragraph>
      <ul className="terms-list">
        <li><strong>Acceso.</strong> El derecho a solicitar acceso a la información personal que tenemos sobre usted.</li>
        <li><strong>Corrección.</strong> El derecho a solicitar que corrijamos información personal inexacta o incompleta.</li>
        <li><strong>Eliminación.</strong> El derecho a solicitar la eliminación de su información personal, sujeto a restricciones 
        legales o contractuales.</li>
        <li><strong>Restricción.</strong> El derecho a solicitar la restricción del procesamiento en ciertas circunstancias.</li>
        <li><strong>Objeción.</strong> El derecho a oponerse a ciertos tipos de procesamiento, incluido el procesamiento basado 
        en intereses legítimos.</li>
        <li><strong>Portabilidad de Datos.</strong> El derecho a solicitar una copia de cierta información personal en un formato 
        estructurado, de uso común y legible por máquina.</li>
      </ul>
      <Paragraph>
        Puede ejercer muchos de estos derechos iniciando sesión en su cuenta y actualizando su información directamente, o 
        contactándonos usando los detalles de contacto en la Sección 13 a continuación. Es posible que necesitemos verificar 
        su identidad antes de responder a ciertas solicitudes.
      </Paragraph>
      <Paragraph>
        Si se encuentra en el EEE, el Reino Unido u otra jurisdicción que proporciona estos derechos, también tiene derecho a 
        presentar una queja ante su autoridad local de protección de datos.
      </Paragraph>

      <Title level={2} className="section-title">12. Privacidad de los Niños</Title>
      <Paragraph>
        El Servicio no está destinado para uso de niños menores de 18 años, y no recopilamos intencionalmente información personal 
        de niños menores de 18 años. Si nos enteramos de que hemos recopilado información personal de un niño menor de 18 años, 
        tomaremos medidas razonables para eliminar dicha información. Si cree que un niño menor de 18 años nos ha proporcionado 
        información personal, contáctenos.
      </Paragraph>

      <Title level={2} className="section-title">13. Cambios a Esta Política de Privacidad</Title>
      <Paragraph>
        Podemos actualizar esta Política de vez en cuando. Cuando lo hagamos, revisaremos la fecha de "Última actualización" en 
        la parte superior de este documento y podemos proporcionar un aviso adicional (como por correo electrónico o notificación 
        en la aplicación) donde lo requiera la ley.
      </Paragraph>
      <Paragraph>
        Su uso continuado del Servicio después de que una Política actualizada entre en vigencia indica su aceptación de la 
        Política actualizada. Si no está de acuerdo con la Política actualizada, debe dejar de usar el Servicio.
      </Paragraph>

      <Title level={2} className="section-title">14. Contáctenos</Title>
      <Paragraph>
        Si tiene alguna pregunta o inquietud sobre esta Política de Privacidad o nuestras prácticas de datos, puede contactarnos en:
      </Paragraph>
      <Paragraph>
        <strong>DivvyStash</strong><br />
        Correo electrónico: support@divvystash.com<br />
        Dirección: 2320 Prosperity Way, Suwanee, GA 30024
      </Paragraph>
    </div>
  );
};
